/* eslint- import/no-extraneous-dependencies */
import React from "react";
import lod_ from "lodash";
import notificationActions from "redux/actions/notificationActions";
import NotificationsNoneOutlinedIcon from "@material-ui/icons/NotificationsNoneOutlined";
import {
	markAllNotificationsAsRead,
	removeNotification,
	selectUnreadCount,
	selectNotificationsItems
} from "redux/reducers/notificationsReducer";
/**
 * #######################################################@
 *
 * Notifications component
 *
 * Used to display notifications
 *
 * #######################################################@
 */
import {
	Menu,
	Icon,
	Tabs,
	Tab,
	Typography,
	Box,
	Button,
	Badge,
	ListItem,
	ListItemText,
	makeStyles,
	Fade
} from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { List, CellMeasurer, CellMeasurerCache } from "react-virtualized";
import AutoSizer from "react-virtualized-auto-sizer";
import NotificationItem from "components/NotificationItem";
import { handleNotificationClick } from "helpers/notifications";
import { t } from "i18next";
import { useTheme } from "theme";

const useNotificationStyles = makeStyles(theme => ({
	menuPaper: {
		width: "40vw",
		minWidth: 320,
		maxWidth: 500,
		height: "70vh",
		maxHeight: "70vh",
		borderRadius: 12,
		boxShadow:
			theme.palette.type === "dark"
				? "0 8px 32px rgba(0, 0, 0, 0.4)"
				: "0 8px 32px rgba(0, 0, 0, 0.12)",
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
		backgroundColor: theme.palette.type === "dark" ? "var(--dark-bg-paper)" : "#fff"
	},
	header: {
		padding: theme.spacing(1.5, 2),
		background: theme.palette.type === "dark" ? "var(--dark-bg-paper)" : "white",
		color: theme.palette.type === "dark" ? "var(--dark-text-primary)" : "#333",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		borderBottom: theme.palette.type === "dark" ? "1px solid var(--dark-border-color)" : "none"
	},
	headerTitle: {
		fontWeight: 600,
		fontSize: "1rem",
		letterSpacing: "0.5px",
		color: theme.palette.type === "dark" ? "var(--dark-text-primary)" : "inherit"
	},
	markAllButton: {
		color: theme.palette.type === "dark" ? "var(--dark-text-secondary)" : "#333",
		borderColor: theme.palette.type === "dark" ? "var(--dark-border-color)" : "#333",
		textTransform: "none",
		fontWeight: 500,
		fontSize: "0.75rem",
		padding: theme.spacing(0.5, 1.5),
		borderRadius: 20,
		transition: "all 0.3s ease",
		"&:hover": {
			backgroundColor: theme.palette.type === "dark" ? "var(--dark-bg-elevated)" : "#333",
			color: "white"
		}
	},
	tabsContainer: {
		borderRadius: 12,
		margin: theme.spacing(1),
		backgroundColor: theme.palette.type === "dark" ? "var(--dark-bg-primary)" : "#ededed",
		padding: theme.spacing(0.7)
	},
	tabs: {
		minHeight: 30,
		padding: theme.spacing(0),
		"& .MuiTabs-indicator": {
			display: "none"
		}
	},
	tab: {
		borderRadius: 8,
		padding: theme.spacing(0),
		flex: 1,
		textTransform: "none",
		minHeight: 30,
		fontWeight: 500,
		fontSize: "0.85",
		transition: "all 0.3s ease",
		color: theme.palette.text.secondary,
		"&.Mui-selected": {
			fontWeight: 600,
			background: theme.palette.type === "dark" ? "var(--dark-bg-elevated)" : "white",
			color: theme.palette.type === "dark" ? "var(--dark-text-primary)" : "#333",
			boxShadow:
				theme.palette.type === "dark"
					? "0 2px 8px rgba(0, 0, 0, 0.3)"
					: "0 2px 8px rgba(0, 0, 0, 0.1)"
		}
	},
	badge: {
		marginLeft: theme.spacing(4)
	},
	errorBadge: {
		"& .MuiBadge-badge": {
			background: "red",
			boxShadow: "0 2px 8px rgba(255, 0, 0, 0.3)"
		}
	},
	secondaryBadge: {
		"& .MuiBadge-badge": {
			background: theme.palette.type === "dark" ? "var(--dark-bg-elevated)" : "lightgray",
			color: theme.palette.type === "dark" ? "var(--dark-text-primary)" : "#333",
			boxShadow:
				theme.palette.type === "dark"
					? "0 2px 8px rgba(0, 0, 0, 0.3)"
					: "0 2px 8px rgba(0, 0, 0, 0.1)"
		}
	},
	listContainer: {
		overflow: "hidden",
		flex: "1 1 auto",
		minHeight: 0,
		backgroundColor: theme.palette.type === "dark" ? "var(--dark-bg-paper)" : "#fff"
	},
	emptyState: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		padding: theme.spacing(6, 3),
		color:
			theme.palette.type === "dark" ? "var(--dark-text-secondary)" : theme.palette.text.secondary
	},
	emptyStateText: {
		fontSize: "0.9375rem",
		fontWeight: 500,
		opacity: 0.6,
		marginTop: theme.spacing(1)
	},
	bellIcon: {
		transition: "all 0.3s ease",
		"&:hover": {
			transform: "scale(1.1) rotate(15deg)"
		}
	},
	iconButton: {
		cursor: "pointer",
		transition: "all 0.3s ease",
		"&:hover": {
			transform: "translateY(-2px)"
		}
	}
}));

const Notifications = ({ classes, boxClasses, itemText }) => {
	const notificationsList = useSelector(selectNotificationsItems);
	const unreadCount = useSelector(selectUnreadCount);
	const dispatch = useDispatch();
	const styles = useNotificationStyles();
	const { isDarkMode } = useTheme();

	const [openMenu, setOpenMenu] = useState(false);
	const [activeTab, setActiveTab] = useState(0); // 0 = All, 1 = Unread
	const listRef = useRef(null);
	const cacheRef = useRef(
		new CellMeasurerCache({
			defaultHeight: 72,
			minHeight: 56,
			fixedWidth: true
		})
	);

	const handleOpenMenu = event => {
		setOpenMenu(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setOpenMenu(false);
		setActiveTab(0); // Reset to "All" tab when closing
	};

	const handleTabChange = (event, newValue) => {
		setActiveTab(newValue);
	};

	/**
	 * Marquer toutes les notifications comme lues
	 */
	const handleMarkAllAsRead = () => {
		const readAt = new Date().toISOString();
		dispatch(markAllNotificationsAsRead({ readAt }));

		dispatch(
			notificationActions.markAllAsRead(
				() => {},
				() => {}
			)
		);
	};

	/**
	 * Supprimer une notification
	 * @param {string} notificationId - ID de la notification
	 */
	const handleDeleteNotification = notificationId => {
		if (lod_.isNil(notificationId) === true) {
			return;
		}

		dispatch(removeNotification({ id: notificationId }));

		dispatch(
			notificationActions.deleteNotification(
				notificationId,
				() => {},
				() => {}
			)
		);
	};

	// Filter notifications based on active tab
	const filteredNotifications = lod_
		.cloneDeep(notificationsList)
		.filter(notification => {
			if (activeTab === 0) {
				// Unread tab
				return notification.read !== true;
			}
			// All tab
			return true;
		})
		.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

	useEffect(() => {
		cacheRef.current.clearAll();
		if (listRef.current) {
			listRef.current.recomputeRowHeights();
		}
	}, [filteredNotifications]);

	const rowRenderer = ({ index, key, parent, style }) => {
		const notification = filteredNotifications[index];

		if (lod_.isNil(notification) === true) {
			return null;
		}

		return (
			<CellMeasurer
				cache={cacheRef.current}
				columnIndex={0}
				rowIndex={index}
				parent={parent}
				key={key}
			>
				{({ registerChild }) => (
					<div ref={registerChild} style={{ ...style, width: "100%" }}>
						<NotificationItem
							key={notification.id || index}
							id={notification.id}
							icon={notification.icon}
							message={notification.message}
							createdAt={notification.createdAt}
							severity={notification.severity}
							read={notification.read}
							onClick={() => handleNotificationClick(notification, dispatch)}
							onDelete={() => handleDeleteNotification(notification.id)}
						/>
					</div>
				)}
			</CellMeasurer>
		);
	};

	const renderMenu = () => (
		<Menu
			anchorEl={openMenu}
			anchorReference={null}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "left"
			}}
			open={Boolean(openMenu)}
			TransitionComponent={Fade}
			classes={{
				paper: styles.menuPaper
			}}
			MenuListProps={{
				disablePadding: true,
				style: {
					display: "flex",
					flexDirection: "column",
					height: "100%",
					overflow: "hidden"
				}
			}}
			onClose={handleCloseMenu}
		>
			{/* Fixed Header */}
			<Box className={styles.header}>
				<Typography className={styles.headerTitle}>{t("NOTIFICATIONS.TITLE")}</Typography>

				<Button
					disabled={unreadCount <= 0}
					variant="outlined"
					size="small"
					onClick={handleMarkAllAsRead}
					className={styles.markAllButton}
				>
					{t("NOTIFICATIONS.markAllAsRead")}
				</Button>
			</Box>

			{/* Fixed Tabs */}
			<Box className={styles.tabsContainer}>
				<Tabs value={activeTab} onChange={handleTabChange} className={styles.tabs}>
					<Tab
						className={styles.tab}
						label={
							<Box display="flex" alignItems="center">
								<span>{t("NOTIFICATIONS.unread")}</span>
								{unreadCount > 0 && (
									<Badge
										badgeContent={unreadCount}
										color="error"
										className={`${styles.badge} ${styles.errorBadge}`}
									/>
								)}
							</Box>
						}
					/>
					<Tab
						className={styles.tab}
						label={
							<Box display="flex" alignItems="center">
								<span>{t("NOTIFICATIONS.all")}</span>
								<Badge
									badgeContent={notificationsList.length}
									color="secondary"
									className={`${styles.badge} ${styles.secondaryBadge}`}
								/>
							</Box>
						}
					/>
				</Tabs>
			</Box>

			{/* Scrollable Notifications List */}
			<Box className={styles.listContainer}>
				{filteredNotifications.length === 0 && (
					<Box className={styles.emptyState}>
						<NotificationsNoneOutlinedIcon
							style={{
								fontSize: 48,
								opacity: isDarkMode === true ? 0.5 : 0.3,
								color: isDarkMode === true ? "var(--dark-text-secondary)" : undefined
							}}
						/>
						<Typography variant="caption" style={{ opacity: 0.7 }} fontWeight="bold">
							{activeTab === 0
								? t("NOTIFICATIONS.noUnreadNotifications")
								: t("NOTIFICATIONS.noNotifications")}
						</Typography>
					</Box>
				)}

				{filteredNotifications.length > 0 && (
					<AutoSizer>
						{({ height, width }) => (
							<List
								ref={listRef}
								height={height}
								width={width}
								rowCount={filteredNotifications.length}
								deferredMeasurementCache={cacheRef.current}
								rowHeight={cacheRef.current.rowHeight}
								rowRenderer={rowRenderer}
								overscanRowCount={6}
							/>
						)}
					</AutoSizer>
				)}
			</Box>
		</Menu>
	);

	return (
		<>
			<ListItem className={classes.item}>
				<Box className={boxClasses} onClick={handleOpenMenu}>
					<Badge
						className={classes.itemIcon}
						color="error"
						overlap="circular"
						badgeContent={unreadCount}
					>
						<Icon className={classes.itemIcon} style={{ color: "white" }}>
							<NotificationsNoneOutlinedIcon />
						</Icon>
					</Badge>
					<ListItemText
						className={itemText}
						disableTypography={true}
						primary={t("NOTIFICATIONS.TITLE")}
						style={{ color: "white" }}
					/>
				</Box>
			</ListItem>
			{renderMenu()}
		</>
	);
};

export default Notifications;
